import { forwardRef, type ComponentRef } from "react";

import { cn } from "../../lib";
import { touchTargetClassName } from "../../lib/touch-target";
import { Box } from "../../primitives/box";
import { Pressable } from "../../primitives/pressable";
import { Icon } from "../icons";
import { radioButtonHitSlop } from "./lib";
import {
  radioButtonIndicatorVariants,
  radioButtonVariants,
  radioCheckTickSize,
} from "./radio-button.variants";
import type { RadioButtonProps } from "./types";

/**
 * RadioButton atom. Plain controlled visual radio control: `checked` + `onChange`.
 * For labelled options in a group, use RadioGroup's RadioGroupItem, whose whole
 * row is the tap target. Standalone, it takes taps 44px across: `hitSlop` on
 * native (`radioButtonHitSlop`), and on web touch screens `touchTargetClassName`.
 */
const RadioButton = forwardRef<ComponentRef<typeof Pressable>, RadioButtonProps>(
  (
    {
      checked,
      onChange,
      id,
      disabled = false,
      size = "md",
      indicator = "fill",
      hitSlop = radioButtonHitSlop[indicator][size],
      className,
      ...rest
    },
    ref,
  ) => (
    <Pressable
      // First, so nothing in it can replace the control's own wiring below
      {...rest}
      ref={ref}
      id={id}
      hitSlop={hitSlop}
      accessibilityRole="radio"
      accessibilityState={{ checked, disabled }}
      disabled={disabled}
      data-state={checked ? "checked" : "unchecked"}
      onClick={() => {
        if (!checked) onChange(true);
      }}
      className={cn(
        "group",
        touchTargetClassName,
        radioButtonVariants({ size, indicator, checked, disabled, self: true }),
        className,
      )}
    >
      <Box className={radioButtonIndicatorVariants({ size, indicator, checked, disabled })}>
        {indicator === "check" && checked ? (
          <Icon
            name="Check"
            size={radioCheckTickSize[size]}
            color="brand-on-primary"
            weight="bold"
          />
        ) : null}
      </Box>
    </Pressable>
  ),
);

RadioButton.displayName = "RadioButton";

export { RadioButton };
